import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked } from '@angular/core';
import { form as signalForm } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';

import { SpColorPickerV2Component, type ColorPickerEventPayload } from '@components/sp-color-picker/src/v2';

import { COLOR_DESCRIPTION_TRANSLATIONS, COLOR_NAME_TRANSLATIONS } from '../../config/translation-keys.const';

import type { ThemeColor, ThemeSelectedColors } from '../../types/theme-color.type';
import { ThemeColorNames } from '../../enums/color-names.enum';

import { ThemeStateService } from '../../state/theme-state.service';

/** The palette as this panel edits it: one colour per slot, keyed by slot name. */
type Palette = Record<ThemeColorNames, string>;

const COLOR_NAMES = Object.values(ThemeColorNames);

/**
 * Every slot present, empty.
 *
 * The form's field tree is derived from the value, so a slot missing from the initial one has no
 * subfield — and the template asks for all five on its first render, before the effect that fills
 * them from the theme has run.
 */
function emptyPalette(): Palette {
    return Object.fromEntries(COLOR_NAMES.map((name) => [name, ''])) as Palette;
}

function toPalette(colors: ThemeSelectedColors): Palette {
    const palette = emptyPalette();

    COLOR_NAMES.forEach((name) => {
        palette[name] = colors.selected.find((color: ThemeColor) => color.name === name)?.value ?? '';
    });

    return palette;
}

function toSelection(palette: Palette): ThemeSelectedColors {
    return {
        selected: COLOR_NAMES.map((name) => ({ name, value: palette[name] })),
        // The palette no longer matches any shipped preset once a slot is edited by hand.
        selectedIndex: null,
    };
}

function isSamePalette(a: Palette, b: Palette): boolean {
    return COLOR_NAMES.every((name) => a[name] === b[name]);
}

@Component({
    selector: 'sp-theme-colors-settings',
    templateUrl: './colors-settings.component.html',
    styleUrls: ['./colors-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SpColorPickerV2Component],
})
export class SpThemeColorsSettingsComponent implements OnDestroy {
    private readonly themeState = inject(ThemeStateService);

    /** The five slots, in the order the panel lists them. */
    public readonly colorNames = COLOR_NAMES;
    public readonly COLOR_NAME_TRANSLATIONS = COLOR_NAME_TRANSLATIONS;
    public readonly COLOR_DESCRIPTION_TRANSLATIONS = COLOR_DESCRIPTION_TRANSLATIONS;

    private readonly model = signal<Palette>(emptyPalette());
    public readonly form = signalForm(this.model);

    /**
     * The palette as it was when the panel opened, for cancel and for closing without saving.
     *
     * A signal so the template and the effects read it the same way everything else here is read;
     * it is written once, the first time the theme reports a palette.
     */
    private readonly initialColors = signal<ThemeSelectedColors | null>(null);
    private isSaved = false;

    constructor() {
        // Theme → form. Also re-runs on the writes this panel makes itself, which is what keeps the
        // pickers showing the value a preset click just applied.
        effect(() => {
            const colors = this.themeState.colors();

            if (!colors) {
                return;
            }

            if (!untracked(this.initialColors)) {
                this.initialColors.set(colors);
            }

            this.model.set(toPalette(colors));
        });

        // Form → theme. Compared by VALUE, not identity: the write above hands back a fresh object
        // every time, and `changeColors` clears `selectedIndex`, so identity always differs. Only a
        // real edit gets through, which is what stops the two effects echoing each other.
        effect(() => {
            const palette = this.form().value();
            const current = untracked(this.themeState.colors);

            if (!current || isSamePalette(palette, toPalette(current))) {
                return;
            }

            this.themeState.changeColors(toSelection(palette));
        });
    }

    public onChangeColor(name: ThemeColorNames, { value }: ColorPickerEventPayload): void {
        // The picker is not a `[formField]` control, so nothing marks the form dirty for us.
        this.form().markAsDirty();
        this.model.update((palette) => ({ ...palette, [name]: value }));
    }

    public onSave(): void {
        this.isSaved = true;
        this.themeState.unselectActiveSetting();
    }

    public onCancel(): void {
        this.restoreInitialColors();
        this.themeState.unselectActiveSetting();
    }

    public ngOnDestroy(): void {
        if (!this.isSaved) {
            this.restoreInitialColors();
        }
    }

    private restoreInitialColors(): void {
        const initial = this.initialColors();

        if (this.form().dirty() && initial) {
            this.themeState.changeColors(initial);
        }
    }
}
